// src/cockpit4/components/LeadNotificationBell.tsx
// ============================================
// 顶部栏右侧「官网留资」通知铃铛（v6.25）
//   - 仅 super_admin 显示（留资含电话，敏感）
//   - 每 60s 轮询 /lead-api/list，未读 = time > 上次已读时间（localStorage）
//   - 点铃铛：红点消失 + 下拉展示最近留资
// ============================================

import { useEffect, useRef, useState } from "react";
import { Bell, Phone } from "lucide-react";
import { useUserStore } from "../../stores/userStore";

interface LeadItem {
  name: string;
  phone: string;
  company: string;
  need: string;
  source: string;
  time: string;
}

const LAST_SEEN_KEY = "xeaos_leads_lastseen";

export default function LeadNotificationBell() {
  const currentUser = useUserStore((s) => s.currentUser);
  const isAdmin = currentUser?.role === "super_admin";
  const [open, setOpen] = useState(false);
  const [leads, setLeads] = useState<LeadItem[]>([]);
  const [newCount, setNewCount] = useState(0);
  const lastSeenRef = useRef<string>(localStorage.getItem(LAST_SEEN_KEY) || "");

  useEffect(() => {
    if (!isAdmin) return;
    const load = async () => {
      try {
        const resp = await fetch("/lead-api/list");
        const data = await resp.json();
        if (data?.success) {
          const items: LeadItem[] = Array.isArray(data.leads) ? data.leads : [];
          setLeads(items.slice(0, 20));
          const lastSeen = lastSeenRef.current;
          const unread = items.filter((l) => !lastSeen || (l.time || "") > lastSeen).length;
          setNewCount(unread);
        }
      } catch {
        /* ignore */
      }
    };
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [isAdmin]);

  const markSeen = () => {
    const now = new Date().toISOString().replace("T", " ").slice(0, 19);
    localStorage.setItem(LAST_SEEN_KEY, now);
    lastSeenRef.current = now;
    setNewCount(0);
  };

  if (!isAdmin) return null;

  return (
    <div className="relative">
      <button
        onClick={() => {
          setOpen((v) => !v);
          if (!open) markSeen();
        }}
        className="relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-mono uppercase tracking-wider bg-white/[0.03] text-gray-400 border border-white/[0.06] hover:text-white hover:border-white/10 transition-all"
        title="官网留资通知"
      >
        <Bell size={13} />
        <span className="hidden lg:inline">留资</span>
        {newCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[15px] h-[15px] px-0.5 rounded-full bg-red-500 text-white text-[8px] font-bold flex items-center justify-center">
            {newCount > 9 ? "9+" : newCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-9 w-80 rounded-lg border border-white/[0.08] bg-[#0F1520]/95 backdrop-blur-xl shadow-2xl overflow-hidden z-[70]">
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06]">
            <span className="text-[10px] font-mono text-gray-300 flex items-center gap-1.5">
              <Phone size={10} className="text-emerald-400" /> 官网留资
            </span>
            <span className="text-[9px] font-mono text-gray-500">{leads.length} 条</span>
          </div>
          <div className="max-h-[320px] overflow-y-auto p-1.5 space-y-1">
            {leads.length === 0 ? (
              <p className="text-[10px] text-gray-600 text-center py-4">暂无留资</p>
            ) : (
              leads.map((l, i) => (
                <div key={i} className="rounded border border-white/[0.05] bg-white/[0.02] p-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] font-bold text-white">{l.name}</span>
                    <a href={`tel:${l.phone}`} className="text-[10px] text-cyan-300 hover:underline">
                      {l.phone}
                    </a>
                    <span className="ml-auto text-[8px] font-mono text-gray-600">
                      {(l.time || "").slice(5, 16)}
                    </span>
                  </div>
                  {l.need && <p className="text-[9px] text-gray-400 mt-0.5 line-clamp-2">{l.need}</p>}
                </div>
              ))
            )}
          </div>
          <div className="px-3 py-2 border-t border-white/[0.06] text-center">
            <span className="text-[9px] font-mono text-gray-600">完整列表：营销中心 → GEO+DSO 控制台 → 官网留资</span>
          </div>
        </div>
      )}
    </div>
  );
}
